import React, { useContext, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { PosDeviceContext } from '../context/PosDeviceContext';
import AppIcon from './icons/AppIcon';
import './MobileBottomNav.css';

/**
 * Role-aware bottom nav for PWA / mobile.
 * Primary targets: Order Creator + Store Manager (also usable by overlapping roles).
 */
const MobileBottomNav = () => {
  const { user } = useContext(AuthContext);
  const { enrolled: posDeviceEnrolled } = useContext(PosDeviceContext);

  const items = useMemo(() => {
    if (!user) return [];
    const role = user.role;

    if (role === 'Order Creator') {
      return [
        { to: '/', end: true, label: 'Home', icon: 'home' },
        { to: '/orders', label: 'Orders', icon: 'orders' },
        { to: '/orders/create', label: 'Create', icon: 'create', primary: true },
        { to: '/flavours', label: 'Catalog', icon: 'catalog' }
      ];
    }

    if (role === 'Store Manager') {
      return [
        { to: '/store-manager', end: true, label: 'Store', icon: 'store' },
        posDeviceEnrolled ? { to: '/pos', label: 'POS', icon: 'pos' } : null,
        { to: '/orders/create', label: 'Create', icon: 'create', primary: true },
        { to: '/orders', label: 'Orders', icon: 'orders' }
      ].filter(Boolean);
    }

    const list = [{ to: '/', end: true, label: 'Home', icon: 'home' }];
    if (!['Store Manager'].includes(role)) {
      list.push({ to: '/orders', label: 'Orders', icon: 'orders' });
    }
    if (['Order Creator', 'Admin', 'Cashier', 'Franchise Admin', 'Store Manager', 'Accountant'].includes(role)) {
      list.push({ to: '/orders/create', label: 'Create', icon: 'create', primary: true });
    }
    if (['Store Manager', 'Admin', 'Franchise Admin'].includes(role)) {
      list.push({ to: '/store-manager', label: 'Store', icon: 'store' });
    }
    if (posDeviceEnrolled && ['Admin', 'Store Manager', 'Cashier', 'Franchise Admin'].includes(role)) {
      list.push({ to: '/pos', label: 'POS', icon: 'pos' });
    }
    return list.slice(0, 5);
  }, [user, posDeviceEnrolled]);

  if (!user || items.length === 0) return null;

  return (
    <nav className="mobile-bottom-nav" aria-label="Primary mobile">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            `mobile-bottom-nav__item${isActive ? ' is-active' : ''}${item.primary ? ' is-primary' : ''}`
          }
        >
          <span className="mobile-bottom-nav__icon" aria-hidden>
            <AppIcon name={item.icon} size={item.primary ? 22 : 20} />
          </span>
          <span className="mobile-bottom-nav__label">{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
};

export default MobileBottomNav;
